import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { Search } from 'lucide-react';

import { useIntl } from '../../util/reactIntl';
import { PAGE_SEARCH } from '../../util/unifiedSearch';

import { whereLabel } from './WhereField';
import { whenLabel } from './WhenField';

import css from './SearchPill.module.css';

/**
 * The two lines of the pill: where, and which space type (plus the dates, when chosen), as the
 * applied search (URL) says.
 *
 * @param {Object} state readSearchState
 * @param {Object} intl
 * @returns {{ where: string, type: string, isEmpty: boolean }}
 */
export const pillSummary = (state = {}, intl) => {
  const placeLabel = whereLabel(state.where, intl);
  const where =
    placeLabel ||
    intl.formatMessage({
      id:
        state.page === PAGE_SEARCH
          ? 'UnifiedSearch.pill.allSwitzerland'
          : 'UnifiedSearch.pill.wherePlaceholder',
    });
  const typeLabel = intl.formatMessage({
    id: state.spaceType ? `UnifiedSearch.type.${state.spaceType}` : 'UnifiedSearch.type.all',
  });
  const when = whenLabel(state.dates, intl);
  const type = when ? `${typeLabel} · ${when}` : typeLabel;
  const isEmpty = !placeLabel && !state.spaceType && !when && state.page !== PAGE_SEARCH;
  return { where, type, isEmpty };
};

/**
 * Summary of the applied search as one button (spec §4.1): line 1 the place (or "Wo suchst du?"),
 * line 2 the space type (or "Alle Räume"). Tapping it opens the editor.
 *
 * Never wider than its container: both lines ellipsise, the button has `min-width: 0`.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.state readSearchState of the page
 * @param {Function} props.onClick
 * @param {'compact'|'large'} [props.variant] compact = topbar, large = home hero on phones
 * @param {string} [props.id]
 * @param {boolean} [props.expanded] whether the editor it opens is open
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const SearchPill = forwardRef((props, ref) => {
  const { state, onClick, variant = 'compact', id, expanded = false, className } = props;
  const intl = useIntl();
  const { where, type, isEmpty } = pillSummary(state, intl);
  const ariaLabel = isEmpty
    ? intl.formatMessage({ id: 'UnifiedSearch.pill.ariaLabelEmpty' })
    : intl.formatMessage({ id: 'UnifiedSearch.pill.ariaLabel' }, { where, type });

  return (
    <button
      ref={ref}
      id={id}
      type="button"
      className={classNames(css.root, variant === 'large' ? css.large : css.compact, className)}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={expanded}
      data-testid="unified-search-pill"
    >
      <span className={css.icon} aria-hidden="true">
        <Search size={variant === 'large' ? 20 : 18} strokeWidth={2} />
      </span>
      <span className={css.text} aria-hidden="true">
        <span className={classNames(css.where, { [css.placeholder]: isEmpty })}>{where}</span>
        <span className={css.type}>{type}</span>
      </span>
    </button>
  );
});

SearchPill.displayName = 'SearchPill';

export default SearchPill;
